import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiFetch } from './api';

const ActiveCourseContext = createContext(null);
export const ACTIVE_COURSE_ROLES = ['instructor', 'program_baskan'];

export const canUseActiveCourse = (role) => ACTIVE_COURSE_ROLES.includes(role);

const normalizeCourseId = (courseId) => (courseId === null || courseId === undefined || courseId === '' ? '' : String(courseId));

export function ActiveCourseProvider({ instructorId, role = 'instructor', children }) {
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [activeCourseId, setActiveCourseIdState] = useState('');

  const enabled = canUseActiveCourse(role);
  const storageKey = enabled && instructorId ? `activeCourse:${role}:${instructorId}` : '';

  useEffect(() => {
    let cancelled = false;

    const loadCourses = async () => {
      if (!enabled || !instructorId) {
        if (!cancelled) {
          setCourses([]);
          setCoursesLoading(false);
          setActiveCourseIdState('');
        }
        return;
      }

      setCoursesLoading(true);
      try {
        const response = await apiFetch('/instructor/courses');
        const data = await response.json();
        if (!cancelled && data.success) {
          const nextCourses = data.courses || [];
          const savedCourseId = normalizeCourseId(sessionStorage.getItem(storageKey));
          const savedCourseExists = nextCourses.some((course) => normalizeCourseId(course.course_id) === savedCourseId);

          setCourses(nextCourses);
          setActiveCourseIdState(savedCourseExists ? savedCourseId : '');
          if (savedCourseId && !savedCourseExists) sessionStorage.removeItem(storageKey);
        }
      } catch (error) {
        if (!cancelled) setCourses([]);
      } finally {
        if (!cancelled) setCoursesLoading(false);
      }
    };

    loadCourses();
    return () => { cancelled = true; };
  }, [enabled, instructorId, storageKey]);

  const setActiveCourseId = useCallback((courseId) => {
    const nextCourseId = normalizeCourseId(courseId);
    setActiveCourseIdState(nextCourseId);
    if (!storageKey) return;
    if (nextCourseId) sessionStorage.setItem(storageKey, nextCourseId);
    else sessionStorage.removeItem(storageKey);
  }, [storageKey]);

  const clearActiveCourse = useCallback(() => setActiveCourseId(''), [setActiveCourseId]);

  const activeCourse = useMemo(
    () => courses.find((course) => normalizeCourseId(course.course_id) === activeCourseId) || null,
    [courses, activeCourseId]
  );

  const value = useMemo(() => ({
    courses,
    coursesLoading,
    activeCourseId,
    activeCourse,
    setActiveCourseId,
    clearActiveCourse,
  }), [courses, coursesLoading, activeCourseId, activeCourse, setActiveCourseId, clearActiveCourse]);

  if (!enabled) return children;
  return <ActiveCourseContext.Provider value={value}>{children}</ActiveCourseContext.Provider>;
}

export function useActiveCourse() {
  return useContext(ActiveCourseContext);
}
